import { useCallback, useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Download, MapPin, PenLine } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { fmtFecha, fmtHora, fmtHoras, hoyISO, inicioMesISO } from "@/lib/format";
import { TIPO_FICHAJE, fichajeEsReal } from "@/lib/fichajeUi";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import MapaFichajeDialog from "@/components/MapaFichajeDialog";
import CorreccionFichajeDialog from "@/components/CorreccionFichajeDialog";
import SolicitudPicadoManualDialog from "@/components/SolicitudPicadoManualDialog";
import PicadosHoyPanel from "@/components/PicadosHoyPanel";

export default function MisFichajes() {
  const [desde, setDesde] = useState(inicioMesISO());
  const [hasta, setHasta] = useState(hoyISO());
  const [fichajes, setFichajes] = useState([]);
  const [resumen, setResumen] = useState(null);
  const [mapaFichaje, setMapaFichaje] = useState(null);
  const [correccion, setCorreccion] = useState(null);
  const [estado, setEstado] = useState(null);
  const [manualInicial, setManualInicial] = useState(null);
  const [manualRegistro, setManualRegistro] = useState(null);
  const [searchParams, setSearchParams] = useSearchParams();

  const marcarNotifLeida = useCallback(async (notifId) => {
    if (!notifId) return;
    try {
      await api.put(`/notificaciones/${encodeURIComponent(notifId)}/leida`);
      window.dispatchEvent(new Event("notificaciones-actualizar"));
    } catch {
      /* ignorar */
    }
  }, []);

  const consumirManualInicial = useCallback(() => setManualInicial(null), []);

  const abrirManualFaltante = (f) => {
    setManualRegistro({
      open: true,
      tipo: f.tipo,
      fecha: f.timestamp,
      dia: f.fecha,
    });
  };

  const abrirFilaRegistro = (f, e) => {
    if (e?.target?.closest("[data-accion-fila]")) return;
    if (f.es_faltante) {
      if (f.es_modificacion && f.fichaje_id) {
        setCorreccion({ fichaje_id: f.fichaje_id, timestamp: f.timestamp });
        return;
      }
      abrirManualFaltante(f);
      return;
    }
    if (f.pendiente_aprobacion) {
      toast.info("Este picado está pendiente de aprobación por el encargado");
      return;
    }
    if (fichajeEsReal(f)) {
      setCorreccion({ fichaje_id: f.id, timestamp: f.timestamp });
    }
  };

  useEffect(() => {
    const diaSolo = searchParams.get("dia");
    const manual = searchParams.get("manual") === "1";
    if (!manual && !diaSolo) return;

    if (manual) {
      const rawFecha = searchParams.get("fecha");
      const fecha = rawFecha ? rawFecha.replace(/ /g, "+") : null;
      const notif = searchParams.get("notif");
      const dia = diaSolo || (fecha ? fecha.slice(0, 10) : null);
      setManualInicial({
        tipo: searchParams.get("tipo"),
        fecha,
        dia,
        modificacion: searchParams.get("modificacion") === "1",
        fichajeId: searchParams.get("fichaje_id"),
      });
      if (dia) {
        setDesde((prev) => (dia < prev ? dia : prev));
        setHasta((prev) => (dia > prev ? dia : prev));
      }
      if (notif) marcarNotifLeida(notif);
    } else if (diaSolo) {
      setDesde((prev) => (diaSolo < prev ? diaSolo : prev));
      setHasta((prev) => (diaSolo > prev ? diaSolo : prev));
    }
    setSearchParams({}, { replace: true });
  }, [searchParams, setSearchParams, marcarNotifLeida]);

  const cargarEstado = useCallback(async () => {
    try {
      const { data } = await api.get("/fichajes/estado");
      setEstado(data);
    } catch (e) {
      toast.error(formatApiError(e));
    }
  }, []);

  const cargarHistorial = useCallback(async () => {
    try {
      const params = { desde, hasta };
      const [f, r] = await Promise.all([
        api.get("/fichajes/registro", { params }),
        api.get("/informes/resumen", { params }),
      ]);
      setFichajes(f.data);
      setResumen(r.data.usuarios[0] || null);
    } catch (e) {
      toast.error(formatApiError(e));
    }
  }, [desde, hasta]);

  const cargar = useCallback(async () => {
    await Promise.all([cargarEstado(), cargarHistorial()]);
  }, [cargarEstado, cargarHistorial]);

  const fichajesPorDia = useMemo(() => {
    const map = new Map();
    for (const f of fichajes) {
      const d = f.fecha || (f.timestamp ? f.timestamp.slice(0, 10) : null) || "sin-fecha";
      if (!map.has(d)) map.set(d, []);
      map.get(d).push(f);
    }
    const ordenar = (filas) =>
      [...filas].sort((a, b) => {
        const ta = a.timestamp || `${a.fecha || "9999"}T${a.es_faltante ? "99:99" : "00:00"}:00`;
        const tb = b.timestamp || `${b.fecha || "9999"}T${b.es_faltante ? "99:99" : "00:00"}:00`;
        return ta.localeCompare(tb);
      });
    return [...map.entries()]
      .sort(([a], [b]) => b.localeCompare(a))
      .map(([dia, filas]) => [dia, ordenar(filas)]);
  }, [fichajes]);

  useEffect(() => {
    cargar();
  }, [cargar]);

  const renderFilaHistorial = (f) => {
    if (f.es_imputacion) {
      return (
        <li
          key={f.id}
          className="flex flex-wrap items-center justify-between gap-2 border-b border-violet-100 bg-violet-50/70 px-4 py-3 last:border-0"
          data-testid={`imputacion-${f.fecha}`}
        >
          <div className="flex min-w-0 flex-1 flex-wrap items-center gap-2">
            <Badge variant="outline" className="border-violet-300 bg-violet-100 text-violet-900">
              {f.label || "Permiso justificado"}
            </Badge>
            <span className="text-sm text-violet-800">Jornada imputada como horas trabajadas</span>
          </div>
          {f.segundos > 0 && (
            <span className="text-sm font-medium tabular-nums text-violet-900">{fmtHoras(f.segundos)}</span>
          )}
        </li>
      );
    }

    const hora = f.timestamp ? fmtHora(f.timestamp) : "—";

    if (f.es_faltante) {
      const meta = TIPO_FICHAJE[f.tipo] || TIPO_FICHAJE.salida;
      const accion = f.es_modificacion ? "Corregir" : "Solicitar";
      return (
        <li
          key={f.id}
          role="button"
          tabIndex={0}
          title={`Pulsa para ${accion.toLowerCase()} este picado`}
          className="flex cursor-pointer flex-wrap items-center justify-between gap-2 border-b border-amber-100 bg-amber-50/60 px-4 py-3 last:border-0 hover:bg-amber-100/70"
          onClick={(e) => abrirFilaRegistro(f, e)}
          onKeyDown={(e) => {
            if (e.key === "Enter" || e.key === " ") {
              e.preventDefault();
              abrirFilaRegistro(f);
            }
          }}
        >
          <div className="flex min-w-0 flex-1 flex-wrap items-center gap-2">
            <span className="w-12 shrink-0 font-medium tabular-nums text-amber-900">{hora}</span>
            <Badge variant="outline" className="border-amber-300 bg-amber-100 text-amber-900">
              Falta {meta.label.toLowerCase()}
            </Badge>
            <span className="text-sm text-amber-800">{f.detalle || `Tramo ${f.franja || f.tramo}`}</span>
          </div>
          <Button
            variant="outline"
            size="sm"
            className="h-8 shrink-0 gap-1 border-amber-300 text-amber-800 hover:bg-amber-100"
            data-accion-fila
            onClick={() => abrirFilaRegistro(f)}
          >
            <PenLine className="h-3.5 w-3.5" />
            {accion}
          </Button>
        </li>
      );
    }

    const meta = TIPO_FICHAJE[f.tipo] || TIPO_FICHAJE.salida;
    const puedeCorregir = fichajeEsReal(f) && !f.pendiente_aprobacion && !f.es_informativo;
    const origen =
      f.etiqueta_permiso ? (
        <Badge variant="outline" className="border-violet-300 bg-violet-50 text-violet-800">
          {f.etiqueta_permiso}
        </Badge>
      ) : f.es_autocierre ? (
        <Badge variant="outline" className="border-amber-300 bg-amber-50 text-amber-800">
          Cierre automático
        </Badge>
      ) : f.es_manual && /corregida tras cierre|salida corregida/i.test(f.justificacion || "") ? (
        <Badge variant="outline" className="border-emerald-300 bg-emerald-50 text-emerald-800">
          Corregida
        </Badge>
      ) : f.pendiente_aprobacion || f.correccion_pendiente ? (
        <Badge
          variant="outline"
          className={
            (f.estado_solicitud || f.correccion_pendiente?.estado) === "pendiente_confirmacion"
              ? "border-indigo-300 bg-indigo-50 text-indigo-800"
              : "border-sky-300 bg-sky-50 text-sky-800"
          }
        >
          {(f.estado_solicitud || f.correccion_pendiente?.estado) === "pendiente_confirmacion"
            ? "Pendiente tu confirmación"
            : f.es_modificacion_pendiente || f.correccion_pendiente
              ? "Corrección pendiente del encargado"
              : "Picado pendiente de aprobación"}
          {f.timestamp_anterior ? ` · era ${fmtHora(f.timestamp_anterior)}` : ""}
        </Badge>
      ) : f.es_manual ? (
        <span className="text-amber-700" title={f.justificacion || ""}>
          {f.origen === "solicitud_aprobada" ? "Manual aprobado" : "Manual"}
          {f.franja ? ` · ${f.franja}` : ""}
        </span>
      ) : f.es_informativo ? (
        <span className="truncate text-rose-800" title={f.justificacion || ""}>
          {f.justificacion || "Registro de visita"}
        </span>
      ) : (
        <span className="text-zinc-500">App</span>
      );

    return (
      <li
        key={f.id}
        role={puedeCorregir ? "button" : undefined}
        tabIndex={puedeCorregir ? 0 : undefined}
        title={puedeCorregir ? "Pulsa para corregir este picado" : undefined}
        className={`flex flex-wrap items-center justify-between gap-2 border-b border-zinc-100 px-4 py-3 last:border-0 ${
          f.es_autocierre ? "bg-amber-50/40" : ""
        } ${puedeCorregir ? "cursor-pointer hover:bg-indigo-50/60" : "hover:bg-zinc-50/80"}`}
        onClick={puedeCorregir ? (e) => abrirFilaRegistro(f, e) : undefined}
        onKeyDown={
          puedeCorregir
            ? (e) => {
                if (e.key === "Enter" || e.key === " ") {
                  e.preventDefault();
                  abrirFilaRegistro(f);
                }
              }
            : undefined
        }
      >
        <div className="flex min-w-0 flex-1 flex-wrap items-center gap-2">
          <span className="w-12 shrink-0 font-medium tabular-nums text-zinc-900">{hora}</span>
          <Badge variant="outline" className={meta.className}>
            {meta.label}
          </Badge>
          <span className="text-sm">{origen}</span>
          {f.latitud != null && (
            <button
              type="button"
              data-accion-fila
              onClick={() => setMapaFichaje(f)}
              className="inline-flex items-center gap-0.5 text-xs text-indigo-600 hover:underline"
            >
              <MapPin className="h-3 w-3" /> Mapa
            </button>
          )}
        </div>
        <div className="shrink-0" data-accion-fila>
          {puedeCorregir ? (
            <Button
              variant="outline"
              size="sm"
              className={`h-8 gap-1 ${f.es_autocierre ? "border-amber-200 text-amber-800" : "border-indigo-200 text-indigo-700"}`}
              onClick={() => abrirFilaRegistro(f)}
            >
              <PenLine className="h-3.5 w-3.5" />
              Corregir
            </Button>
          ) : f.pendiente_aprobacion || f.correccion_pendiente ? (
            <span className="text-xs text-sky-700">
              {(f.estado_solicitud || f.correccion_pendiente?.estado) === "pendiente_confirmacion"
                ? "Pendiente tu confirmación"
                : "Pendiente del encargado"}
            </span>
          ) : null}
        </div>
      </li>
    );
  };

  const exportar = async () => {
    try {
      const res = await api.get("/informes/export", { params: { desde, hasta }, responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `mis_fichajes_${desde}_${hasta}.csv`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  return (
    <div className="space-y-5" data-testid="mis-fichajes-page">
      <div>
        <h1 className="font-display text-2xl font-semibold tracking-tight">Picados</h1>
        <p className="text-sm text-zinc-500">
          Revisa el registro de hoy, corrige horas o añade los picados que falten. Tu encargado debe aprobar los cambios.
        </p>
      </div>

      <PicadosHoyPanel
        estado={estado}
        onRefresh={cargar}
        manualInicial={manualInicial}
        onManualInicialConsumido={consumirManualInicial}
      />

      <div>
        <h2 className="font-display text-lg font-semibold tracking-tight text-zinc-800">Historial</h2>
        <p className="text-xs text-zinc-500">
          Pulsa una fila para solicitar un picado faltante o corregir uno registrado.
        </p>
      </div>

      <div className="flex flex-wrap items-end gap-3">
        <div className="space-y-1">
          <label className="text-[11px] font-semibold uppercase tracking-[0.1em] text-zinc-500">Desde</label>
          <Input type="date" value={desde} onChange={(e) => setDesde(e.target.value)} data-testid="mis-desde-input" className="w-40" />
        </div>
        <div className="space-y-1">
          <label className="text-[11px] font-semibold uppercase tracking-[0.1em] text-zinc-500">Hasta</label>
          <Input type="date" value={hasta} onChange={(e) => setHasta(e.target.value)} data-testid="mis-hasta-input" className="w-40" />
        </div>
        <Button variant="outline" onClick={exportar} data-testid="exportar-mis-btn" className="gap-1.5 rounded-lg">
          <Download className="h-4 w-4" strokeWidth={1.5} />
          Exportar CSV
        </Button>
      </div>

      <div className="grid grid-cols-3 gap-3">
        <div className="rounded-xl border border-zinc-200 bg-white p-4 shadow-sm">
          <p className="text-[11px] font-semibold uppercase tracking-[0.1em] text-zinc-500">Horas</p>
          <p className="font-display mt-1 text-2xl font-light" data-testid="mis-horas-totales">
            {resumen ? fmtHoras(resumen.segundos) : "0h 00m"}
          </p>
        </div>
        <div className="rounded-xl border border-zinc-200 bg-white p-4 shadow-sm">
          <p className="text-[11px] font-semibold uppercase tracking-[0.1em] text-zinc-500">Días</p>
          <p className="font-display mt-1 text-2xl font-light" data-testid="mis-dias-trabajados">
            {resumen ? resumen.dias_trabajados : 0}
          </p>
        </div>
        <div className="rounded-xl border border-zinc-200 bg-white p-4 shadow-sm">
          <p className="text-[11px] font-semibold uppercase tracking-[0.1em] text-zinc-500">Bolsa</p>
          <p
            className={`font-display mt-1 text-2xl font-light ${resumen && resumen.saldo_segundos < 0 ? "text-red-600" : "text-green-600"}`}
            data-testid="mis-bolsa-horas"
          >
            {resumen ? `${resumen.saldo_segundos < 0 ? "-" : "+"}${fmtHoras(Math.abs(resumen.saldo_segundos))}` : "+0h 00m"}
          </p>
        </div>
      </div>

      <div className="space-y-4" data-testid="mis-fichajes-tabla">
        {fichajes.length === 0 ? (
          <div className="overflow-hidden rounded-xl border border-zinc-200 bg-white shadow-sm">
            <p className="p-6 text-center text-sm text-zinc-400">No hay fichajes en este periodo.</p>
          </div>
        ) : (
          fichajesPorDia.map(([dia, filas]) => {
            const faltantes = filas.filter((f) => f.es_faltante).length;
            const imputacion = filas.find((f) => f.es_imputacion);
            return (
              <section
                key={dia}
                className="overflow-hidden rounded-xl border border-zinc-200 bg-white shadow-sm"
                data-testid={`mis-fichajes-dia-${dia}`}
              >
                <div className="border-b border-indigo-100 bg-gradient-to-r from-indigo-50/90 to-sky-50/50 px-4 py-3">
                  <div className="flex flex-wrap items-center gap-2">
                    <p className="font-display text-base font-semibold text-zinc-900">
                      {dia !== "sin-fecha" ? fmtFecha(`${dia}T12:00:00`) : "Sin fecha"}
                    </p>
                    {imputacion?.label && (
                      <Badge variant="outline" className="border-violet-300 bg-violet-50 text-violet-800">
                        {imputacion.label}
                      </Badge>
                    )}
                  </div>
                  <p className="text-xs text-zinc-500">
                    {filas.length} registro{filas.length !== 1 ? "s" : ""}
                    {faltantes > 0 && (
                      <span className="ml-2 font-medium text-amber-700">
                        · {faltantes} picado{faltantes !== 1 ? "s" : ""} pendiente{faltantes !== 1 ? "s" : ""}
                      </span>
                    )}
                  </p>
                </div>
                <ul className="text-sm">{filas.map((f) => renderFilaHistorial(f))}</ul>
              </section>
            );
          })
        )}
      </div>

      <MapaFichajeDialog fichaje={mapaFichaje} onClose={() => setMapaFichaje(null)} />
      <CorreccionFichajeDialog item={correccion} onClose={() => setCorreccion(null)} onSuccess={cargar} />
      <SolicitudPicadoManualDialog
        estado={estado}
        open={Boolean(manualRegistro?.open)}
        onOpenChange={(open) => {
          if (!open) setManualRegistro(null);
        }}
        defaultTipo={manualRegistro?.tipo}
        defaultTimestamp={manualRegistro?.fecha}
        defaultDia={manualRegistro?.dia}
        onSuccess={cargar}
        testId="manual-registro-dialog"
      />
    </div>
  );
}
